/**
 * @file INCSUBUserDetailUpdate.js
 * @author Vishal Lohbande
 * @description
 * This React component renders a form to update user details such as name, email, phone number, and date of birth.
 * It compares initial and updated data and triggers an API call to persist changes.
 * Handles error states and input validations.
 * Uses Redux for updating global state and navigation routing.
 * @props {Object} personalDetailData - Initial user data used to pre-fill the form.
 * @props {Function} functionCall - Optional callback function to execute after successful update.
 */

import { useState } from 'react';
import "./styleUserDetailsupdate.css";
import { useDispatch, useSelector } from 'react-redux';
import {
    setIsUpdateDetails,
    setProfileInfo,
    setSelectedUser,
    setUserList,
    setUsrDetailsRoute
} from '../../features/users/userSlice';
import { getUserUpdateDetails } from '../../services/userServices';
import { toast } from 'react-toastify';


function INCSUBUserDetailUpdate({ personalDetailData: initialData, functionCall = null, setIsUpdateDetails }) {
    const dispatch = useDispatch();
    const profileInfo = useSelector((state) => state.user.profileInfo);
    // Form state initialization
    const [personalDetailData, setPersonalDetailData] = useState(initialData);
    const [userFullName, setUserFullName] = useState(initialData?.userFullName);
    const [userEmail, setUserEmail] = useState(initialData?.userEmail);
    const [userMobileNumber, setUserMobileNumber] = useState(initialData?.userMobileNumber);
    const [userDob, setUserDob] = useState(initialData?.userDob);
    const userList = useSelector((state) => state.user.userList)
    const [isDisabled, setIsDisabled] = useState(false);

    // Error states
    const [numberErrorFlag, setNumberErrorFlag] = useState(false);
    const [numberErrorMsg, setNumberErrorMsg] = useState('Enter valid number');
    const [emailErrorFlag, setEmailErrorFlag] = useState(false);
    const [emailErrorMsg, setEmailErrorMsg] = useState('Enter valid email');
    const [nameErrorFlag, setNameErrorFlag] = useState(false);

    /**
     * Validate name input
     * @param {string} name
     */
    const nameValidateFunction = (name) => {
        name.trim() === '' ? setNameErrorFlag(true) : setNameErrorFlag(false);
    };

    /**
     * Validate email input format
     * @param {string} email
     * @returns {boolean}
     */
    function isValidEmail(email) {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
    }

    /**
     * Prepare request payload for API
     */
    const requestParams = {
        userId: personalDetailData?.userId,
        userFullName: userFullName?.trim(),
        userEmail: userEmail,
        userMobileNumber: userMobileNumber,
        userDob: userDob,
        userPassword: "null",
        status: personalDetailData?.status
    };

    /**
     * API call to update user details if changed
     */
    const fetchPersonalDetailsUpdate = async (params) => {
        const hasChanges =
            initialData?.userFullName !== userFullName ||
            initialData?.userEmail !== userEmail ||
            initialData?.userDob !== userDob ||
            initialData?.userMobileNumber !== userMobileNumber;
        if (!hasChanges) return dispatch(setIsUpdateDetails(false));

        setIsDisabled(true);
        try {
            const response = await getUserUpdateDetails(params);

            if (response?.request?.status === 200) {
                const updatedData = {
                    ...personalDetailData,
                    userFullName: userFullName.trim(),
                    userEmail,
                    userMobileNumber,
                    userName: userMobileNumber,
                    userDob: userDob || '',
                };

                setPersonalDetailData(updatedData);
                localStorage.setItem('userPersonalDetails', JSON.stringify(updatedData));
                localStorage.setItem('userPersonalDetails', JSON.stringify(updatedData));
                if (updatedData.userId === JSON.parse(localStorage?.getItem('userData')).userId) {
                    localStorage.setItem('userData', JSON.stringify({ ...updatedData, userRole: profileInfo.userRole }));
                    dispatch(setProfileInfo({ ...updatedData, userRole: profileInfo.userRole }))
                }

                dispatch(setUsrDetailsRoute('/personaldetails'));
                dispatch(setSelectedUser(updatedData));
                dispatch(setIsUpdateDetails(false));
                const updatedUserList = userList && userList.length > 0 && userList?.map((user) =>
                    user.userId === updatedData.userId
                        ? { ...user, ...updatedData } // merge, updatedData overrides user fields
                        : user
                );
                dispatch(setUserList(updatedUserList))
                toast.success("User has beed updated successfully")
                functionCall?.();
            } else {
                handleUpdateErrors(response);
            }
        } catch (err) {
            toast.error("An error occurred during update.");
        } finally {
            setIsDisabled(false);
        }
    };

    /**
     * Handle update response errors
     * @param {Object} response
     */
    const handleUpdateErrors = (response) => {
        localStorage.setItem('userPersonalDetails', JSON.stringify(initialData));

        switch (response?.request?.status) {
            case 409:
                const message = response.response?.data?.message;
                if (message === 'Mobile Number already present') {
                    setNumberErrorMsg(message);
                    setNumberErrorFlag(true);
                } else {
                    setEmailErrorMsg(message);
                    setEmailErrorFlag(true);
                }
                break;
            default:
                toast.error("Update failed. Please try again.");
        }
    };

    /**
     * Reset form to initial state
     */
    const handleCancel = () => {

        setUserFullName(initialData?.userFullName);
        setUserEmail(initialData?.userEmail);
        setUserMobileNumber(initialData?.userMobileNumber);
        setUserDob(initialData?.userDob);

        setNumberErrorFlag(false);
        setEmailErrorFlag(false);
        setNameErrorFlag(false);
        dispatch(setIsUpdateDetails(false))
        setNumberErrorMsg('Enter valid number');
        setEmailErrorMsg('Enter valid email');
    };

    return (
        <div className='INCSUBusrdetailupdate'>
            <div className='INCSUBusrdetailupdate_container'>
                <div className='INCSUBusrdetailupdate_details_container'>
                    {/* Close icon to cancel edit */}
                    <svg
                        onClick={() => dispatch(setIsUpdateDetails(false))}
                        className='cross_icon'
                        width="20"
                        height="19"
                        viewBox="0 0 20 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path fillRule="evenodd" clipRule="evenodd" d="M1.46318 1.47683C1.77048...Z" fill="black" />
                    </svg>

                    <div className='INCSUBusrdetailupdate_details_header'>Update User</div>

                    <div className='INCSUBusrdetailupdate_details'>
                        <form className='INCSUBusrdetailupdate_details_form'>
                            {/* Full Name */}
                            <div className='INCSUBusrdetailupdate_details_input_container'>
                                <label>Name</label>
                                <input
                                    style={{ borderColor: nameErrorFlag ? 'red' : undefined }}
                                    value={userFullName}
                                    onChange={(e) => {
                                        setUserFullName(e.target.value);
                                        nameValidateFunction(e.target.value);
                                    }}
                                />
                                {nameErrorFlag && <p className='error-msg'>Enter your full name</p>}
                            </div>

                            {/* Email and Phone */}
                            <div className='INCSUBusrdetailupdate_details_input_container half'>
                                <div className='INCSUBusrdetailupdate_details_input_part'>
                                    <label>Email Id</label>
                                    <input
                                        style={{ borderColor: emailErrorFlag ? 'red' : undefined }}
                                        value={userEmail}
                                        onChange={(e) => {
                                            setUserEmail(e.target.value);
                                            if (!isValidEmail(e.target.value)) {
                                                setEmailErrorFlag(true);
                                                setEmailErrorMsg('Enter valid email');
                                            } else {
                                                setEmailErrorFlag(false);
                                            }
                                        }}
                                    />
                                    {emailErrorFlag && <p className='error-msg'>{emailErrorMsg}</p>}
                                </div>

                                <div className='INCSUBusrdetailupdate_details_input_part'>
                                    <label>Phone Number</label>
                                    <input
                                        style={{ borderColor: numberErrorFlag ? 'red' : undefined }}
                                        maxLength={10}
                                        value={userMobileNumber}
                                        onChange={(e) => {
                                            const number = e.target.value;
                                            setUserMobileNumber(number);
                                            if (number.length < 10 || number === '') {
                                                setNumberErrorFlag(true);
                                                setNumberErrorMsg('Enter valid number');
                                            } else {
                                                setNumberErrorFlag(false);
                                            }
                                        }}
                                    />
                                    {numberErrorFlag && <p className='error-msg'>{numberErrorMsg}</p>}
                                </div>
                            </div>

                            {/* Date of Birth */}
                            {/* <div className='INCSUBusrdetailupdate_details_input_container half'>
                                <div className='INCSUBusrdetailupdate_details_input_part'>
                                    <label>Date of Birth</label>
                                    <input
                                        type='date'
                                        value={userDob !== '' ? userDob : ''}
                                        onChange={(e) => setUserDob(e.target.value)}
                                    />
                                </div>
                            </div> */}
                        </form>
                    </div>

                    {/* Action Buttons */}
                    <div className='INCSUBusrupdate_btn_container'>
                        <button
                            disabled={isDisabled}
                            className='INCSUBusrupdate_btn submit'
                            onClick={() => {
                                if (!nameErrorFlag && !numberErrorFlag && !emailErrorFlag) {
                                    console.log("h")
                                    fetchPersonalDetailsUpdate(requestParams);
                                } else {
                                    dispatch(setIsUpdateDetails(false));
                                    console.log("q")
                                }
                            }}
                        >
                            {isDisabled ? <div className='btnLoader'></div> : "Update"}
                        </button>
                        <button className='INCSUBusrupdate_btn cancel' onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default INCSUBUserDetailUpdate;
